import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { PremiumPrice } from 'src/api/internal/schema';
import { CommitmentUpsellSource } from 'src/api/tracking/types';
import { TrackingApi } from 'src/api';
import { Button, Icon } from 'src/components';
import { useUserContext } from 'src/context/user';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import { pickGoodLuckCelebrationVideo } from 'src/apps/Path/PreCheckpointScreen/goodLuckVideos';
import useSafePush from 'src/hooks/useSafePush';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { useViewItemListEvent } from '../hooks/useViewItemListEvent';
import { buildCommitmentPlansUrl } from '../utils/commitmentPageUrl';
import { getPlansPageSource } from '../utils/getPlansPageSource';
import { CommitmentPaywall } from '../CommitmentPaywall';
import * as S from './styles';

const LOCATION = 'commitment_upsell' as const;

const msgs = defineMessages({
  headline: {
    id: 'commitment_led_upsell_headline',
    defaultMessage: 'How committed are you?',
  },
  subhead: {
    id: 'commitment_led_upsell_subhead',
    defaultMessage: 'Commit to learning {language} alongside 250,000+ PRO users.',
  },
  oneMonth: {
    id: 'commitment_led_upsell_1_month',
    defaultMessage: '1 month',
  },
  twelveMonths: {
    id: 'new_plans_card_12_months',
    defaultMessage: '12 months',
  },
  lifetime: {
    id: 'ea_plans_lifetime',
  },
  mostPopular: {
    id: 'onboarding_upsell_all_pro_plans_badge_popular_plan',
    defaultMessage: 'Most popular',
  },
  cta: {
    id: 'commitment_led_upsell_cta',
    defaultMessage: 'Commit',
  },
  continueForFree: {
    id: 'milestone_upsell_module_skip',
    defaultMessage: 'Continue for free',
  },
  muteVideo: {
    id: 'commitment_led_upsell_mute_video',
    defaultMessage: 'Mute video',
  },
  unmuteVideo: {
    id: 'commitment_led_upsell_unmute_video',
    defaultMessage: 'Unmute video',
  },
  fallbackLanguage: {
    id: 'commitment_led_upsell_fallback_language',
    defaultMessage: 'Commit to learning a language alongside 250,000+ PRO users.',
  },
});

export type CommitmentLedPlansPageProps = {
  prices: PremiumPrice[];
  selectedPrice: PremiumPrice | null;
  setSelectedPrice: (price: PremiumPrice) => void;
  onSubscribe: (price?: PremiumPrice) => void;
  onClose?: () => void;
  source?: CommitmentUpsellSource;
  /** Plans page opens on the paywall. The commitment page still starts on the upsell. */
  startOnPaywall?: boolean;
};

export const CommitmentLedPlansPage: FC<CommitmentLedPlansPageProps> = ({
  prices,
  selectedPrice,
  setSelectedPrice,
  onSubscribe,
  onClose,
  source,
  startOnPaywall = false,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const { languagePair } = useCurrentLanguagePair();
  const languagePairId = getLanguagePairId();
  const isPro = !!user?.isPro;
  // Read at event time rather than render: the URL fallback needs `window`.
  const getSource = () => source ?? getPlansPageSource();

  const monthlyPlan = prices.find(p => p.paymentPeriod === 'month') || null;
  const yearlyPlan = prices.find(p => p.paymentPeriod === 'year') || null;
  const lifetimePlan = prices.find(p => p.paymentPeriod === 'lifetime') || null;

  const displayedPrices = useMemo(
    () => [monthlyPlan, yearlyPlan, lifetimePlan].filter(Boolean) as PremiumPrice[],
    [monthlyPlan, yearlyPlan, lifetimePlan],
  );

  const [activePrice, setActivePrice] = useState<PremiumPrice | null>(null);
  const [muted, setMuted] = useState(false);
  const showPaywall = startOnPaywall;
  const clipRef = useRef<HTMLVideoElement>(null);
  const { safePush } = useSafePush();

  useEffect(() => {
    if (startOnPaywall) return;

    void TrackingApi.trackUserAction('screen_viewed', LOCATION, languagePairId, isPro, {
      source: getSource(),
    });
    // Fire only once on mount. The paywall records its own view when it is the first screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewItemListEvent = useViewItemListEvent(displayedPrices);

  useEffect(() => {
    if (viewItemListEvent) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(viewItemListEvent);
    }
  }, [viewItemListEvent]);

  useEffect(() => {
    if (activePrice) return;

    if (selectedPrice) {
      setActivePrice(selectedPrice);
      return;
    }

    const defaultPlan = yearlyPlan || displayedPrices[0];
    if (defaultPlan) {
      setSelectedPrice(defaultPlan);
      setActivePrice(defaultPlan);
    }
  }, [activePrice, selectedPrice, yearlyPlan, displayedPrices, setSelectedPrice]);

  const video = useMemo(() => {
    const locale = languagePair?.targetLanguageLocale;
    const forLanguage = pickGoodLuckCelebrationVideo(locale);
    if (forLanguage || locale) return forLanguage;
    return pickGoodLuckCelebrationVideo('en');
  }, [languagePair?.targetLanguageLocale]);

  useEffect(() => {
    const clip = clipRef.current;
    if (!clip || !video || typeof clip.play !== 'function') return;

    let cancelled = false;

    const start = async () => {
      clip.muted = false;
      clip.volume = 1;
      try {
        await clip.play();
      } catch {
        if (cancelled) return;
        // Browsers block autoplay with sound until the user has interacted.
        // Play muted so the clip still starts, and leave the mute control on.
        clip.muted = true;
        setMuted(true);
        try {
          await clip.play();
        } catch {
          // Playback is blocked entirely. Leave the first frame in place.
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
    };
  }, [video]);

  const toggleSound = () => {
    const clip = clipRef.current;
    const nextMuted = !muted;
    setMuted(nextMuted);
    if (!clip) return;

    clip.muted = nextMuted;
    if (nextMuted || !clip.paused || clip.ended) return;

    clip.volume = 1;
    void clip.play().catch(() => undefined);
  };

  const subhead = languagePair?.targetLanguageName
    ? intl.formatMessage(msgs.subhead, { language: languagePair.targetLanguageName })
    : intl.formatMessage(msgs.fallbackLanguage);

  const selectPrice = (price: PremiumPrice) => {
    setActivePrice(price);
    setSelectedPrice(price);
    void TrackingApi.trackUserAction('plan_selected', LOCATION, languagePairId, isPro, {
      plan_id: price.key,
      plan: price.paymentPeriod,
      price: String(price.finalPrice),
      step: showPaywall ? 'paywall' : 'upsell',
      source: getSource(),
    });
  };

  const dismiss = (cta: 'continue_for_free' | 'keep_learning_for_free') => {
    const trackingData = { cta, source: getSource() };
    void TrackingApi.trackUserAction(
      'clicked_free_link',
      LOCATION,
      languagePairId,
      isPro,
      trackingData,
    );
    void TrackingApi.trackUserAction(
      'paywall_dismissed',
      LOCATION,
      languagePairId,
      isPro,
      trackingData,
    );
    onClose?.();
  };

  const handleCommit = () => {
    void TrackingApi.trackUserAction('clicked_commit', LOCATION, languagePairId, isPro, {
      plan: activePrice?.paymentPeriod || '',
      price: String(activePrice?.finalPrice || ''),
      source: getSource(),
    });
    void safePush(buildCommitmentPlansUrl(getSource()));
  };

  const continueToCheckout = () => {
    if (!activePrice) return;

    void TrackingApi.trackUserAction('clicked_subscribe', LOCATION, languagePairId, isPro, {
      cta: 'continue_to_checkout',
      plan: activePrice.paymentPeriod,
      price: String(activePrice.finalPrice),
      source: getSource(),
    });
    onSubscribe(activePrice);
  };

  const formatPrice = (price: PremiumPrice) =>
    intl.formatNumber(price.finalPrice, {
      style: 'currency',
      currency: price.currency,
      minimumFractionDigits: Number.isInteger(price.finalPrice) ? 0 : 2,
    });

  const planLabel = (price: PremiumPrice) => {
    if (price.paymentPeriod === 'month') return intl.formatMessage(msgs.oneMonth);
    if (price.paymentPeriod === 'year') return intl.formatMessage(msgs.twelveMonths);
    return intl.formatMessage(msgs.lifetime);
  };

  if (showPaywall) {
    return (
      <CommitmentPaywall
        prices={displayedPrices}
        activePrice={activePrice}
        onSelectPrice={selectPrice}
        onCheckout={continueToCheckout}
        source={source}
        onClose={onClose ? () => dismiss('keep_learning_for_free') : undefined}
        stillSrc={video?.src}
      />
    );
  }

  return (
    <S.Page data-testid="commitment-led-plans-page">
      {video && (
        <S.Stage data-testid="commitment-led-video">
          <S.Clip
            ref={clipRef}
            src={video.src}
            autoPlay
            muted={muted}
            playsInline
            preload="auto"
            controls={false}
            data-testid="commitment-led-clip"
          />
        </S.Stage>
      )}

      <S.Panel>
        <S.Headline>{intl.formatMessage(msgs.headline)}</S.Headline>
        <S.Subhead>{subhead}</S.Subhead>

        <S.PlanList aria-label={intl.formatMessage(msgs.headline)}>
          {displayedPrices.map(price => {
            const selected = activePrice?.paymentPeriod === price.paymentPeriod;
            const isYearly = price.paymentPeriod === 'year';

            return (
              <S.PlanOption
                key={price.key || price.paymentPeriod}
                type="button"
                $selected={selected}
                onClick={() => selectPrice(price)}
                data-testid="price-card"
                aria-pressed={selected}
              >
                {isYearly ? (
                  <S.PopularBadge>{intl.formatMessage(msgs.mostPopular)}</S.PopularBadge>
                ) : null}
                <S.Radio $selected={selected} aria-hidden>
                  {selected ? <S.RadioCheck name="check" /> : null}
                </S.Radio>
                <S.PlanLabel>{planLabel(price)}</S.PlanLabel>
                <S.PlanPrice>{formatPrice(price)}</S.PlanPrice>
              </S.PlanOption>
            );
          })}
        </S.PlanList>

        <S.Actions>
          <Button
            buttonType="primary"
            buttonSize="large"
            wide
            onClick={handleCommit}
            data-testid="subscribe"
            disabled={!activePrice}
          >
            {intl.formatMessage(msgs.cta)}
          </Button>

          {onClose ? (
            <S.ContinueForFree
              type="button"
              onClick={() => dismiss('continue_for_free')}
              data-testid="continue-for-free"
            >
              {intl.formatMessage(msgs.continueForFree)}
            </S.ContinueForFree>
          ) : null}
        </S.Actions>
      </S.Panel>

      {video ? (
        <S.MuteButton
          type="button"
          onClick={toggleSound}
          aria-pressed={muted}
          aria-label={intl.formatMessage(muted ? msgs.unmuteVideo : msgs.muteVideo)}
          data-testid="commitment-led-mute"
        >
          <Icon name={muted ? 'soundMuted' : 'soundFull'} />
        </S.MuteButton>
      ) : null}
    </S.Page>
  );
};
